import React from "react";
import { CircleMarker, Marker, Popup, Tooltip } from "react-leaflet";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { shallowEqual } from "react-redux";
import { Button, ButtonGroup } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  capitalizeFirstLetter,
  dynamicTextColor,
  getBusFillingColor,
  getSpeedWithUnit,
  normalizeTimestamp,
  returnRadius,
  showColorStatus,
  timeFromNow,
  timeManipulation,
  toMinutesAndSeconds,
  translateString,
  valueFromLocalStorage,
} from "../utils/helpers";
import mapBusesIcons from "./MapBusesIcons";
import { dispatchCoordinates, fetchDataActiveBus, resetDataActiveBus, setActiveBus } from "../redux/actions";
import subcontractorIcon from "../assets/icons/subcontractorIcon.svg";
import variableColors from "../_App.module.scss";
import iconMonitored from "../assets/icons/iconMonitored.svg";
import iconNotMonitored from "../assets/icons/iconNotMonitored.svg";
import unknownIcon from "../assets/icons/unknownIcon.svg";
import { configAgencyName } from "../config";
import TripProgressBar from "./helpers/TripProgressBar";
import { TripProgress } from "./helpers/Helpers";
import infoIcon from "../assets/icons/infoIcon.svg";
import OccupancyStatusIcon from "./helpers/OccupancyStatusIcon";
import { AgencyContext } from "../hooks/AgencyContext";
import { getHeadwayStatusColor } from "../utils/swiftlyHelpers";
import { useSelectedLanguage } from "../hooks/useSelectedLanguage";

const { customSecondaryColor, primaryColor, secondaryColor, customTertiaryColor, darkColor } = variableColors;

const ActiveBusDiv = styled.div`
  border: 2px solid ${primaryColor};
  background-color: ${customTertiaryColor};
  font-weight: bold;
  font-size: 120%;
`;

export const BusTooltip = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  gap: 0.25rem;
`;

export const HR = styled.hr`
  border-top: 2px dotted ${secondaryColor};
  margin: 0.25rem auto;
`;

const ButtonContainer = styled.div`
  button.show-more-button {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    line-height: 1.4;
    padding: 1px;
    font-weight: bold;
  }
`;

export const OccupancyStatusDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  gap: 0.5rem;
`;

//* The main component
const MapBusesComponent = ({
  showLineNumbers,
  busesOnMap,
  showDeviations,
  moreActions,
}: {
  showLineNumbers: boolean;
  busesOnMap: BusInfo[];
  showDeviations: boolean;
  moreActions: boolean;
}): JSX.Element => {
  const dispatch: Dispatch = useAppDispatch();

  const [activeBus, currentZoomFromRedux]: [{ busId: string }, number] = useAppSelector(
    (state: RootState) => [state?.buses?.activeBus, state?.constantValues?.currentZoom],
    shallowEqual
  );

  const pathOptionsRed = { fillColor: "OrangeRed", color: "Red", fillOpacity: 0.8 };
  const pathOptionsYellow = { fillColor: "Yellow", color: "Yellow", fillOpacity: 0.8 };

  const selectBus = (busId: string, routeId?: string, direction?: string): void => {
    // console.log({ busId, routeId, direction });
    dispatch(setActiveBus(busId));
    if (routeId && direction) {
      dispatch(fetchDataActiveBus(routeId, direction));
    } else {
      dispatch(resetDataActiveBus());
    }
  };

  const busesOnMapIcons: JSX.Element[] =
    busesOnMap &&
    busesOnMap?.map((elem: BusInfo, index: number) => (
      <Marker
        key={elem.id}
        position={{
          lat: elem?.loc?.lat ?? 0,
          lng: elem?.loc?.lon ?? 0,
        }}
        icon={mapBusesIcons(
          elem?.colorStatus,
          elem?.addedStatus,
          elem?.isAtStop === true,
          activeBus && activeBus.busId === elem?.id,
          elem?.loc?.heading,
          false,
          ""
        )}
        eventHandlers={
          moreActions
            ? {
                click: async (): Promise<void> => {
                  await selectBus(elem.id, elem.routeId, elem.direction);
                  currentZoomFromRedux &&
                    (await dispatch(dispatchCoordinates(elem.loc.lat, elem.loc.lon, currentZoomFromRedux)));
                },
              }
            : {
                click: (): void => {
                  dispatch(setActiveBus(elem.id));
                },
              }
        }
      >
        {showLineNumbers ? (
          <Tooltip
            //* offset switched off
            // offset={index % 2 === 0 ? [9, 0] : [-9, 0]}
            interactive={true}
            permanent={true}
            direction={index % 2 === 0 ? "right" : "left"}
            className={elem.subcontractorPKS ? "subcontractor-vehicle" : ""}
          >
            <BusTooltipComponent elem={elem} activeBus={activeBus} />
          </Tooltip>
        ) : null}
        {/* //* Popup */}
        {activeBus && (activeBus.busId === "" || activeBus.busId === elem.id) ? <BusIconPopup bus={elem} /> : null}
        {elem.colorStatus === "orangered" ||
        elem.colorStatus === "yellow" ||
        elem.addedStatus === "busTooFast" ||
        elem.addedStatus === "delayedBus" ? (
          <CircleMarker
            center={{
              lat: elem?.loc?.lat,
              lng: elem?.loc?.lon,
            }}
            pathOptions={elem.schAdh > 0 ? pathOptionsRed : pathOptionsYellow}
            radius={
              !showDeviations
                ? 0
                : elem?.colorStatus === "orangered" ||
                    elem?.colorStatus === "yellow" ||
                    elem?.addedStatus === "busTooFast" ||
                    elem?.addedStatus === "delayedBus"
                  ? returnRadius(elem.schAdh)
                  : 0
            }
          />
        ) : null}
      </Marker>
    ));

  return <React.Fragment>{busesOnMapIcons}</React.Fragment>;
};

export default MapBusesComponent;

//* Tooltip
export const BusTooltipComponent = ({ elem, activeBus }: { elem: BusInfo; activeBus: { busId: string } }): JSX.Element => {
  const { t } = useTranslation();

  return (
    <BusTooltip>
      {activeBus && activeBus.busId === elem.id ? (
        <ActiveBusDiv>
          {elem?.acronym || elem?.routeShortName || elem?.routeId || t("b/d")} {"| "}
          <span style={{ color: customSecondaryColor }}>{elem?.vehicleName || elem?.id || `${t("b/d")}`}</span>
        </ActiveBusDiv>
      ) : (
        <React.Fragment>
          <span
            className="span_bold"
            style={{
              display: "inline-block",
              textAlign: "center",
              minWidth: "32px",
              color: elem?.routeInfoAdded?.textColor,
              backgroundColor: elem?.routeInfoAdded?.color,
              padding: "1px 3px",
              border: `1px solid ${darkColor}`,
            }}
          >
            {elem?.acronym || elem?.routeShortName || elem?.routeId || t("b/d")}
          </span>
          <span style={{ color: customSecondaryColor, fontWeight: "600" }}>
            {elem?.vehicleName || elem?.id || `${t("b/d")}`}
          </span>
        </React.Fragment>
      )}
      {elem.subcontractorPKS ? (
        <span className="span_sub">
          <img width={16} height={16} src={subcontractorIcon} alt="Subcontractor Icon" />
        </span>
      ) : null}
    </BusTooltip>
  );
};

//* Popup
export const BusIconPopup = ({ bus }: { bus: BusInfo }): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  //* AgencyContext
  const { speedUnit, timeZone } = React.useContext(AgencyContext) as AgencyContextType;
  //* Custom Hook
  const [selectedLanguage] = useSelectedLanguage();

  //* showMoreBusDetails from localStorage
  const [showMoreBusDetails, setShowMoreBusDetails] = React.useState<boolean>(
    valueFromLocalStorage("showMoreBusDetails") ? JSON.parse(valueFromLocalStorage("showMoreBusDetails") as string) : false
  );

  //* Set showMoreBusDetails in localStorage
  React.useEffect(() => {
    localStorage.setItem("showMoreBusDetails", JSON.stringify(showMoreBusDetails));
  }, [showMoreBusDetails]);

  bus.colorStatus = bus.colorStatus ? bus.colorStatus : showColorStatus(bus.schAdh);
  // console.log("bus:", bus);

  return (
    <Popup>
      {configAgencyName === ("PoznanPKS" as string) ? null : (
        <React.Fragment>
          {t("Zmiana")}: <span className="span_bold">{bus?.addedShift ?? t("b/d")}</span>
          <br />
        </React.Fragment>
      )}
      {t("Linia")}:{" "}
      <span
        className="span_bold"
        style={{
          color: bus?.routeInfoAdded?.textColor,
          backgroundColor: bus?.routeInfoAdded?.color,
          display: "inline-block",
          textAlign: "center",
          minWidth: "32px",
          padding: "1px 3px",
          border: `1px solid ${darkColor}`,
        }}
      >
        {(bus.routeShortName || bus.routeId) ?? t("b/d")}
      </span>
      <br />
      <HR />
      {t("Zadanie")}: <span className="span_bold">{bus.block ?? t("b/d")}</span>
      <br />
      {t("Start zadania")}: <span className="span_bold">{bus?.blockInfo?.startTime ?? t("b/d")}</span>
      <br />
      {t("Koniec zadania")}: <span className="span_bold">{bus?.blockInfo?.endTime ?? t("b/d")}</span>
      <br />
      <HR />
      {t("Kurs")}: <span className="span_bold">{bus.trip ?? t("b/d")}</span>
      <br />
      {t("Start kursu")}: <span className="span_bold">{bus?.blockInfo?.trip?.startTime ?? t("b/d")}</span>
      <br />
      {t("Koniec kursu")}: <span className="span_bold">{bus?.blockInfo?.trip?.endTime ?? t("b/d")}</span>
      {showMoreBusDetails ? (
        <React.Fragment>
          <br />
          {t("Status kursu")}:{" "}
          <span>
            <span className="span_bold">
              {bus?.cancelReenableData?.scheduleRelationship
                ? t(capitalizeFirstLetter(bus?.cancelReenableData?.scheduleRelationship))
                : t("b/d")}
            </span>
            {" ( "}
            <span>{t("isCanceled")}</span> <span className="span_bold">{t(bus?.isCanceled.toString())}</span>
            {" ) "}
          </span>
          <br />
          <HR />
          {t("Postęp")}:{" "}
          <span>
            <TripProgress currentBus={bus} speedUnit={speedUnit as string} />
          </span>
          <br />
          {bus?.currentProgress || bus?.currentProgress === 0 ? (
            <TripProgressBar bus={bus} strokeLinecap={"round"} realRouteColor={false} />
          ) : null}
          <HR />
          {t("Dokąd")}: <span className="span_bold">{bus.headsign ?? t("b/d")}</span>
          <br />
          {t("Następna stacja")}:{" "}
          <span className="span_bold">
            {bus?.nextStopName ?? t("b/d")} ({bus?.nextStopId ?? t("b/d")})
          </span>
          <br />
          {t("Jest na przystanku")}:{" "}
          <span className="span_bold">
            {" "}
            {bus?.isAtStop === true ? t("Tak") : bus?.isAtStop === false ? t("Nie") : t("b/d")}
          </span>
          <br />
          {t("Prędkość")}:{" "}
          <span className="span_bold"> {getSpeedWithUnit(bus?.loc?.speed, speedUnit as string) ?? t("b/d")}</span>
        </React.Fragment>
      ) : null}
      <br />
      <HR />
      {t("Pojazd")} - {t("Nazwa")} <span className="span_bold">{bus?.vehicleName || `${t("b/d")}`}</span>
      <br />
      {t("Pojazd")} - {t("Id")} <span className="span_bold">{bus?.id || `${t("b/d")}`}</span>
      <br />
      {t("Ostatnia aktualizacja")}{" "}
      <span className="span_bold">
        {timeManipulation(normalizeTimestamp(bus?.loc?.time), timeZone as string) || t("b/d")}
      </span>
      <br />
      {t("Zaktualizowano")}:{" "}
      <span className="span_bold">{timeFromNow(normalizeTimestamp(bus?.loc?.time), selectedLanguage) || t("b/d")}</span>
      {showMoreBusDetails ? (
        <React.Fragment>
          <br />
          {t("Pojazd")} - {t("Dodatkowe info")} <span className="span_bold">{bus?.pksName || `${t("b/d")}`}</span>
          <br />
          {t("VehicleType")} <span className="span_bold">{t(bus?.vehicleTypeName) || `${t("b/d")}`}</span>
          <br />
          {t("Status")}:{" "}
          <span className="span_bold" style={{ background: bus?.colorStatus ?? "" }}>
            {translateString(bus?.schAdhStr ?? t("b/d"))}
          </span>
          <br />
          {t("Kalendarz")}: <span className="span_bold">{bus?.blockInfo?.serviceId || t("b/d")}</span>
          <br />
          {t("Stan zadania")}: <span className="span_bold">{bus.blockMthd ?? t("b/d")}</span>
          <br />
          {t("Kierunek kursu")}: <span className="span_bold">{bus.direction || t("b/d")}</span>
          <br />
          {t("Azymut")}: <span className="span_bold">{bus.loc.heading ? `${bus.loc.heading}\u00B0 ` : t("b/d")}</span>
          <br />
          {t("Real-Time")}:
          <span style={{ marginLeft: "0.25rem" }} className="span_bold">
            {bus?.monitored?.toString() ?? t("b/d")}
            <img
              width={18}
              height={18}
              src={
                bus?.monitored?.toString() === "true"
                  ? iconMonitored
                  : bus?.monitored?.toString() === "false"
                    ? iconNotMonitored
                    : unknownIcon
              }
              alt="Monitored / Not Monitored Icon"
              style={{ marginLeft: "0.25rem" }}
            />
          </span>
          {configAgencyName === ("PoznanPKS" as string) ? (
            <React.Fragment>
              <br />
              {t("Podwykonawca")}: <span className="span_bold">{bus.subcontractorPKS?.toString()}</span>
              {bus.subcontractorPKS ? (
                <span style={{ marginLeft: "0.25rem", color: secondaryColor }} className="span_italic">
                  <img
                    width={16}
                    height={16}
                    src={subcontractorIcon}
                    alt="Subcontractor Icon"
                    style={{ marginLeft: "0.25rem" }}
                  />
                </span>
              ) : null}
            </React.Fragment>
          ) : null}
          <br />
          <OccupancyStatusDiv>
            {t("Status zajętości")}: <OccupancyStatusIcon busInfo={bus} isHistorical={false} addText={true} isBold={true} />
          </OccupancyStatusDiv>
          {t("Zapełnienie")}:{" "}
          <span
            className="span_bold"
            style={{ backgroundColor: getBusFillingColor(bus?.passengerFullness as number), padding: "1px 3px" }}
          >
            {bus?.passengerFullness && bus?.passengerFullness !== -1
              ? `${Number(bus?.passengerFullness).toFixed(0)}%`
              : t("b/d")}
          </span>
          <br />
          {t("Headway")}:{" "}
          <span className="span_bold">{bus?.headway === -1 ? t("b/d") : toMinutesAndSeconds(bus?.headway)}</span>
          <br />
          {t("Headway Status")}:{" "}
          <span
            className="span_bold"
            style={{
              background: getHeadwayStatusColor(bus?.headwayDeviationCategory),
              color: dynamicTextColor(getHeadwayStatusColor(bus?.headwayDeviationCategory)),
              fontWeight: "bold",
              padding: "1px 4px",
            }}
          >
            {t(bus?.headwayDeviationCategory) || `${t("b/d")}`}
          </span>
        </React.Fragment>
      ) : null}
      <ButtonContainer>
        <HR />
        <ButtonGroup className="w-100">
          <Button
            variant={showMoreBusDetails ? "light" : "outline-dark"}
            onClick={() => setShowMoreBusDetails(!showMoreBusDetails)}
            className="show-more-button w-75"
            size="sm"
          >
            {showMoreBusDetails ? t("Pokaż mniej") : t("Pokaż więcej")}
          </Button>
          <Button
            disabled={location?.pathname.includes("/views/bus") ? true : false}
            variant="info"
            size="sm"
            className="show-more-button w-25"
            onClick={() => navigate(`/views/bus/${bus?.id}`)}
          >
            <img src={infoIcon} alt="Info Icon" />
          </Button>
        </ButtonGroup>
      </ButtonContainer>
    </Popup>
  );
};
